import { Link } from "@mui/material";

import { routes } from "@/global/routes";
import {
  faqs,
  howItWorks,
  AnalyticsHeader,
  NewYorkYankeesTerms,
  purchaseSeatLanguages,
  NewYorkYankeesTCListItems,
} from "./helpers";
import { vendorUUIDs } from "../../constants";
import { DefaultRegistrationTerms } from "../../default_terms_and_conditions";

import LogoImage from "public/images/newyorkyankees/yankees.png";
import Background from "public/images/newyorkyankees/yankees-background.jpg";
import StaticSeatMapImage from "public/images/newyorkyankees/yankeessuite_static_seatmap_image.jpg";
import FindGamesBanner from "public/images/newyorkyankees/NYY_25_SavannahBananas_WebBanner_1486x148_R5.jpg";

import type { TeamConfig } from "../types";

const config: TeamConfig = {
  teamSchema: "newyorkyankees_bananas_flex",
  browserTitle: "Savannah Bananas + NYY Flex Pack",
  packages: false,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "",
  mlbTeamId: 147,
  teamName: "New York Yankees",

  buckets: {
    enabled: true,
    prioritizeMinGameSelectionCount: true,
    list: [
      {
        id: 1,
        min: 1,
        max: 1,
        name: "Pick 1",
        isPrimary: true,
        closeWhenHitMax: true,
        dontCloseWhenHitMin: true,
        description: "Pick 1 Savannah Bananas game to continue.",
      },
      {
        id: 2,
        min: 3,
        max: 99,
        name: "Pick 3",
        closeWhenHitMax: true,
        dontCloseWhenHitMin: true,
        description: "Pick 3 New York Yankees games to continue.",
      },
    ],
  },

  // seat map configs
  seatMapConfigs: {
    venueId: "nam-us-10171-yankees-sec",
    mapId: "blockmap2d",
    // isView3dButtonHidden: true,
    // isIoMedia3dViewEnabled: true,
    // staticSeatMapImage: StaticSeatMapImage.src,
    // customSeatMapStaticImageStyle: {
    //   display: "block",
    //   width: "60%",
    //   marginX: "auto",
    // },
  },

  // gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: routes.redeem,
  },

  // purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
  },

  // disabled routes
  disabledRoutes: [routes.giftCard],

  // switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: true,
  inventoryType: "SEAT-LEVEL",

  // purchase seats config
  purchaseSeatConfigs: {
    minGameSelectionCount: 4,
    minSeatLimit: 1,
    isSimilarSeatForAllEvents: false,
    allowManualCartInSimilar: false,
    isCheckoutEditSeatsEnabled: false,
    isDiffSeatAlertEnabled: false,
    defaultSeatCount: 2,
    checkoutTimerEnabled: false,
    isPerksEnabled: false,
  },

  // feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // intercom settings
  intercomConfigs: {
    isIntercomEnabled: false,
    allowedPages: [],
  },

  // checkout validations
  checkoutValidation: {
    allowedZipFailCount: Infinity,
    allowedPaymentFailCount: Infinity,
  },

  // analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "",
    attrkPixelId: "",
    beaconId: "",
    AnalyticsHeader,
  },

  // redirect configs
  redirectConfigs: {
    internalRedirectUrl: "",
    externalRedirectUrl: "",
  },

  refreshInventoryWhenEventClick: false,

  isSaveCardAllowed: true,

  // urls
  accountManagerUrl: "https://am.ticketmaster.com/nyyreg/",

  // vendor
  platform: vendorUUIDs.ticketmaster,

  // okta config
  oktaIssuer: "",
  oktaClientId: "",

  isBrainTreePaymentEnabled: false,

  // images
  images: {
    logoNameImage: "",
    seatMap3dPreviewImage: "",
    faviconImage: LogoImage.src,
    authBackgroundImage: Background.src,
    checkoutStatusPageImage: Background.src,
    findGamesBannerImage: FindGamesBanner.src,
    logoImage: LogoImage.src + "?width=128&height=66",
  },

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },

    featuredGameCard: {
      showLogoWithTitle: false,
      hideOverlayLogo: true,
    },

    bottomBar: {
      showGameSelectionProgress: true,
    },

    gameFiltersSettings: {
      hideSectionFilter: false,
      hideAccessibilityFilter: false,
      disableSeatCounterAfterFirstEventHeld: true,
    },

    shouldDisplayServiceFee: false,
    shouldAddServiceFeeWithPerkPrice: true,

    customAdaLabel: (
      <>
        To purchase accessible seating locations, please contact the Box Office
        at{" "}
        <Link
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
        >
          (212) YANKEES
        </Link>
        .
      </>
    ),
  },

  colors: {
    primary: "#132448",
  },

  howItWorks: howItWorks,
  faqs: faqs,

  languages: {
    topNavMenuItems: <NewYorkYankeesTCListItems />,
    checkoutTermsAndCondition: <NewYorkYankeesTerms />,
    registrationTermsAndCondition: <DefaultRegistrationTerms />,

    purchaseSeats: purchaseSeatLanguages,

    purchaseGiftCard: null,
    redeemGiftCard: null,

    checkoutSuccessMessage: "Your purchase is complete!",

    giftCard: {
      title: "",
      createGiftCard: {
        description: "",
        recipient: {
          sendNowFalse: {
            description: "",
          },
          sendNowTrue: {
            description: "",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description: "",
        },
        logout: {
          description: "",
        },
        success: {
          description: "",
        },
      },
    },

    signInAuthText:
      "Connect your mlb.com account to continue with seamless access.",
  },
};

export default config;
